import { buildChunkManifest, readChunkedObjectOfferKillSwitch, type ChunkManifest } from "./chunkManifest";
import { readChunkedObjectRuntimeEnv } from "./chunkedObjectVideoPlan";

/**
 * The persisted upload row fields a re-mint needs to rebuild its chunked-object manifest. Declared
 * structurally so both apps' own `FileUploadSession` types satisfy it without either importing the
 * other's DB types.
 */
export interface PersistedChunkedObjectRow {
	id: string;
	transport?: unknown;
	fileSize: number;
	chunkSizeBytes?: unknown;
}

/**
 * Rebuild the byte-offset manifest a persisted chunked-object row needs to be re-minted on the same
 * lane. Returns null for every row that must go back as plain multipart — the offer kill switch is
 * off, a pre-plan-3a row, a row whose persisted chunk size is unusable, or one whose chunk count no
 * longer matches the manifest (its chunks were sliced with a different size, so the presigned parts
 * could never line up).
 *
 * Shared by contact-admin and contact-async, which both re-mint persisted targets and must decide
 * identically; a private copy in either app is how the kill switch came to be honoured on the fresh
 * plan path (`chunkedObjectVideoPlan.derivePlan`) but not on the re-mint/resume path.
 *
 * The kill switch is defence in depth here — the server is authoritative on transport and falls back
 * on its own — but an operator who flips it off expects no further chunked-object offers at all, not
 * "no NEW ones while every reloaded tab keeps offering".
 */
export function rebuildPersistedChunkManifest(
	session: PersistedChunkedObjectRow,
	totalChunks: number,
	options?: { env?: unknown }
): ChunkManifest | null {
	if (session.transport !== "chunked-object") return null;
	if (readChunkedObjectOfferKillSwitch(options?.env ?? readChunkedObjectRuntimeEnv())) {
		console.warn("[Upload] Chunked-object offer kill switch is off; re-minting the persisted row as multipart", {
			sessionId: session.id
		});
		return null;
	}
	if (!Number.isSafeInteger(session.chunkSizeBytes) || (session.chunkSizeBytes as number) <= 0) {
		console.warn("[Upload] Persisted chunked-object row has no usable chunk size; re-minting as multipart", {
			sessionId: session.id,
			chunkSizeBytes: session.chunkSizeBytes
		});
		return null;
	}
	const built = buildChunkManifest(session.fileSize, session.chunkSizeBytes as number);
	if (!built.ok) {
		console.warn("[Upload] Persisted chunked-object row cannot rebuild its manifest; re-minting as multipart", {
			sessionId: session.id,
			reason: built.reason
		});
		return null;
	}
	if (built.manifest.parts.length !== totalChunks) {
		console.warn("[Upload] Persisted chunked-object manifest disagrees with the row's chunk count; re-minting as multipart", {
			sessionId: session.id,
			totalChunks,
			manifestParts: built.manifest.parts.length,
			chunkSizeBytes: session.chunkSizeBytes
		});
		return null;
	}
	return built.manifest;
}
